import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

export const usePWANavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isExitingRef = useRef(false);
  const toastRef = useRef(null);

  useEffect(() => {
    // Check if running as PWA
    const isPWA = () => {
      return (
        window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone ||
        document.referrer.includes("android-app://")
      );
    };

    if (!isPWA()) return;

    // Add initial history entry
    window.history.pushState({ pwa: true }, "", window.location.href);

    const handlePopState = (e) => {
      if (e.state && e.state.pwa) {
        handleBackButton();
      }
    };

    const handleBackButton = () => {
      // If we're not on the home route, let React Router handle it
      if (location.pathname !== "/" && location.pathname !== "/home") {
        navigate(-1);
        window.history.pushState({ pwa: true }, "", window.location.href);
        return;
      }

      // If on home route, handle exit logic
      if (isExitingRef.current) {
        // Actually exit
        window.history.go(-1);
        return;
      }

      // First back press on home
      isExitingRef.current = true;
      window.history.pushState({ pwa: true }, "", window.location.href);

      showExitConfirmation();

      // Reset after 2 seconds
      setTimeout(() => {
        isExitingRef.current = false;
      }, 2000);
    };

    const showExitConfirmation = () => {
      // Remove existing toast
      if (toastRef.current) {
        toastRef.current.remove();
      }

      const toast = document.createElement("div");
      toast.textContent = "Press back again to exit";
      toast.style.cssText = `
        position: fixed;
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: #333;
        color: white;
        padding: 10px 20px;
        border-radius: 5px;
        z-index: 1000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      `;
      document.body.appendChild(toast);
      toastRef.current = toast;

      setTimeout(() => {
        if (toast.parentNode) {
          toast.remove();
        }
      }, 2000);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      if (toastRef.current) {
        toastRef.current.remove();
      }
    };
  }, [navigate, location.pathname]);
};

export const PWAWrapper = () => {
  usePWANavigation();
};
